<template>
  <div :id="dropzoneId" ref='dropzone' :class="{'dropzone-single': !uploadMultiple}" class="dropzone"
       enctype="multipart/form-data" method="post">
    <div v-if="isOnlyButton" class="dz-default dz-message">
      <button :class="allButtonClasses" :disabled="disabled" data-type>
        <span :class="iconClass"></span>
        {{button.text}}
      </button>
    </div>
    <div v-else class="dz-default dz-message">
      <button v-if="!disabled || readonly" :disabled="readonly" type="button" :aria-required="required" :aria-describedby="ariaDescribedby">
        <icon :class="isImageUpload ? 'image-upload' : 'upload'"></icon>
        <span class="text-block">
          <span>{{dropFileText}}</span>
          <template v-if="allowsMultipleFiles">
            <span><small>Individual File size limit is {{individualMaxFileSize}} MB</small></span>
            <span><small>Total File size limit is {{totalMaxFileSize}} MB</small></span>
          </template>
          <template v-else-if="allowImageMinMax">
            <template v-if="imageWidthMax && imageHeightMax">
              <span><small>Max image resolution {{imageWidthMax}}px x {{imageHeightMax}}px</small></span>
            </template>
            <template v-if="imageWidthMin && imageHeightMin">
              <span><small>Min image resolution {{imageWidthMin}}px x {{imageHeightMin}}px</small></span>
            </template>
          </template>
          <template v-else>
            <span><small>File size limit is {{dropzoneOptions.maxFilesize}} MB</small></span>
          </template>
          <span v-if="isFileTypeRestricted"><small>{{fileRestrictionFriendlyText}}</small></span>
          <span :class="allButtonClasses">Choose {{isImageUpload ? 'image' : 'file'}}</span>
        </span>
      </button>
      <div v-else class="col py-2 d-flex justify-content-center align-items-center">
        <div class="icon blocked mr-3 align-self-center"></div>
        <label class="p-sm font-weight-normal text-left mb-0">Cannot upload or delete files</label>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Dropzone from 'dropzone';
  import SelectedItemMixin from '../../formability/organization_settings/mixins/selectedItemMixin'
  import ExifDataRemover from "../services/exifDataRemover"
  import {Hash} from "~/interfaces/hashInterfaces"
  import heic2any from "heic2any"
  import {Logger} from "~/services/logger"

  export default Vue.extend({
    components: {},
    mixins: [SelectedItemMixin],
    props: {
      url: {
        type: String,
        default: null,
      },
      /**
       * Controls if dropzone will upload multiple files in one call
       * I believe we always want this to be false?
       */
      uploadMultiple: {
        type: Boolean,
        default: false,
      },
      name: {
        type: String,
        default: null,
      },
      acceptedFiles: {
        type: String,
        default: null,
      },
      isOnlyButton: {
        type: Boolean,
        default: false,
      },
      button: {
        type: Object,
        default: () => {
          return { text: "Choose File", icon: null };
        },
      },
      autoProcessQueue: {
        type: Boolean,
        default: false,
      },
      buttonClass: {
        type: String,
        default: "btn blue",
      },
      maxFiles: {
        type: Number,
        default: null, // specifically null means unlimited, undefined means 1 is allowed
      },
      propertyClientId: {
        type: String,
      },
      uploadParams: {
        type: Object,
        default: () => {
          return {}
        },
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * Shows dropzone but upload not enabled - useful for "mock" version of submission
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      individualMaxFileSize: {
        type: Number,
        default: 256,
      },
      totalMaxFileSize: {
        type: Number,
        default: 256,
      },
      isFileTypeRestricted: {
        type: Boolean,
        default: false,
      },
      fileRestrictionFriendlyText: {
        type: String,
        default: '',
      },
      id: {
        type: String,
      },
      isImageUpload: {
        type: Boolean,
        default: false,
      },
      // when true, enforce image min/max dimensions
      allowImageMinMax: {
        type: Boolean,
        default: false,
      },
      imageHeightMin: {
        type: Number,
      },
      imageWidthMin: {
        type: Number,
      },
      imageHeightMax: {
        type: Number,
      },
      imageWidthMax: {
        type: Number,
      },
      invalidFileTypeMessage: {
        type: String,
        default: "You can't upload files of this type.",
      },
      required: {
        type: Boolean,
        default: false,
      },
      ariaDescribedby: {
        type: String,
      },
      initialFiles: {
        type: Array,
        default: () => {
          return []
        },
      },
    },
    mounted() {
      if (!this.disabled) {
        (<any>$(this.$refs.dropzone)).dropzone(this.dropzoneOptions);
      }
    },
    data() {
      const vueComponent = this;
      const dropzoneOptional: Hash = {}
      if (this.isImageUpload) {
        dropzoneOptional.transformFile = async function transformFile(file, done) {
          const exifDataRemover = new ExifDataRemover(file)
          await exifDataRemover.processFile()
          done(exifDataRemover.file)
        }
      }

      return {
        iconClass: this.button.icon,
        Dropzone: Dropzone,
        dropzoneOptions: {
          autoProcessQueue: this.autoProcessQueue,
          headers: {
            'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
          },
          success: function (file, response) {
            // tslint:disable-next-line
            eval(response);
            Logger.debug('Dropzone', 'emit', 'success')
            vueComponent.$emit('success', { file, response });
          },
          accept: function (file, done) {
            Logger.debug('Dropzone', 'emit', 'onAccept')
            if (file.status === Dropzone.CANCELED) return

            if (vueComponent.allowImageMinMax) {
              file.acceptDimensions = function () {
                done();
              };
              file.rejectDimensions = function () {
                const minimumSizeStatement = `the minimum size is ${vueComponent.imageWidthMin}px x ${vueComponent.imageHeightMin}px`;
                const maximumSizeStatement = `the maximum size is ${vueComponent.imageWidthMax}px x ${vueComponent.imageHeightMax}px`;
                done(`Image does not meet the size requirements
                  ${!!vueComponent.imageWidthMin && !!vueComponent.imageHeightMin ? minimumSizeStatement : ''}
                  ${!!vueComponent.imageWidthMin && !!vueComponent.imageHeightMin && !!vueComponent.imageWidthMax && !!vueComponent.imageHeightMax ? ' and ' : ''}
                  ${!!vueComponent.imageWidthMax && !!vueComponent.imageHeightMax ? maximumSizeStatement : ''} `,
                )
              }
            } else {
              if (isHEIC(file)) {
                convertHeicImage(this, file) // convert to jpeg and add new file
                this.cancelUpload(file) // set canceled status on file and prevent upload
                this.removeFile(file) // remove file from dropzone including ui
                Logger.debug('Dropzone', 'emit', 'converting')
                vueComponent.$emit('converting', file);
              } else {
                Logger.debug('Dropzone', 'emit', 'file-accepted')
                vueComponent.$emit('file-accepted', file);
                done();
              }
            }
          },
          acceptedFiles: this.acceptedFiles,
          maxFilesize: this.individualMaxFileSize,
          dictFileTooBig: `The size of your file cannot exceed ${this.individualMaxFileSize} MB. It has not been uploaded.`,
          dictInvalidFileType: this.invalidFileTypeMessage,
          url: this.url,
          maxFiles: this.maxFiles,
          maxfilesexceeded: function (file) {
            if (this.isImageUpload && this.maxFiles !== null) {
              this.removeAllFiles()
              this.addFile(file)
            }
          },
          parallelUploads: 20,
          timeout: 100000,
          disable: this.disabled,
          uploadMultiple: this.uploadMultiple,
          /**
           * is function so changes to +uploadParams+ are observed for each upload
           */
          params: () => {
            return {
              name: this.name,
              property_client_id: this.propertyClientId,
              ...this.uploadParams,
            }
          },
          addRemoveLinks: !this.isOnlyButton,
          init: function () {
            Logger.debug('Dropzone', 'emit', 'init')
            vueComponent.$emit('init', this)
            this.on("thumbnail", function (file) {
              Logger.debug('Dropzone', 'emit', 'onThumbnail')
              vueComponent.$emit('onThumbnail', file, this);
              if (vueComponent.allowImageMinMax) {
                if ((!!vueComponent.imageWidthMax && file.width > vueComponent.imageWidthMax) ||
                  (!!vueComponent.imageHeightMax && file.height > vueComponent.imageHeightMax) ||
                  (!!vueComponent.imageHeightMin && file.height < vueComponent.imageHeightMin) ||
                  (!!vueComponent.imageWidthMin && file.width < vueComponent.imageHeightMin)) {
                  file.rejectDimensions()
                  Logger.debug('Dropzone', 'emit', 'file-reject-dimensions')
                  vueComponent.$emit('file-reject-dimensions', file)
                } else {
                  file.acceptDimensions()
                  Logger.debug('Dropzone', 'emit', 'file-accept-dimensions')
                  vueComponent.$emit('file-accept-dimensions', file)
                }
              }
            })
            this.on("addedfile", function (file) {
              if (isHEIC(file)) {
                convertHeicImage(this, file) // convert to jpeg and add new file
                this.cancelUpload(file) // set canceled status on file and prevent upload
                this.removeFile(file) // remove file from dropzone including ui
                Logger.debug('Dropzone', 'emit', 'converting')
                vueComponent.$emit('converting', file);
              } else {
                Logger.debug('Dropzone', 'emit', 'file-added')
                vueComponent.$emit('file-added', file);
              }
            })
            this.on("removedfile", function (file) {
              Logger.debug('Dropzone', 'emit', 'file-removed')
              vueComponent.$emit('file-removed', file);
            })
            this.on("error", function (file, message) {
              Logger.debug('Dropzone', 'emit', 'error')
              vueComponent.$emit('error', { file, message });
            })
          },
          createImageThumbnails: !this.isOnlyButton,
          previewTemplate: this.isOnlyButton ? `
          <div></div>
          ` : `
            <div class="dz-preview dz-file-preview">
              <div class="dz-controllers">
                <span class="icon close-prompt dz-error-mark"><div data-dz-remove>X</div></span>
              </div>

              <div class="dz-image">
                <img data-dz-thumbnail>
              </div>

              <p class="dz-error-message" data-dz-errormessage></p>
              <p class="dz-filename" data-dz-name></p>
              <p class="dz-size" data-dz-size></p>

              <div class="dz-progress">
                <span class="dz-upload" data-dz-uploadprogress></span>
              </div>
            </div>
          `,
          ...dropzoneOptional,
        },
      }
    },
    computed: {
      dropFileText(): string {
        if (this.allowsMultipleFiles) {
          if (this.name) {
            return `Drop ${this.name} ${this.isImageUpload ? 'images' : 'files'} here to upload`
          } else {
            return `Drop your files here to upload`
          }
        } else {
          if (this.name) {
            return `Drop ${this.name} ${this.isImageUpload ? 'image' : 'file'} here to upload`
          } else {
            return `Drop your file here to upload`
          }
        }
      },
      selector(): string {
        // prepending the propertyClientId with f_ to ensure our selector doesn't
        // start with a number, which leads to errors
        return this.propertyClientId ? `f_${this.propertyClientId}` : this.name;
      },
      dropzoneId(): string {
        return this.id || `${this.selector}-dropzone`
      },
      allButtonClasses() {
        return [
          this.buttonClass,
          {
            disabled: this.disabled,
          },
        ]
      },
      allowsMultipleFiles(): boolean {
        return this.maxFiles === null || this.maxFiles > 1
      },
    },
  })

  /**
   * HEIC/HEIF files are not processable by browsers (and therefore the cropper plugin we use)
   * Use heic2any plugin to convert to jpeg
   *
   * @param dropzone the dropzone where it was uploaded
   * @param file the original heic/heif file
   */
  function convertHeicImage(dropzone, file) {
    const blobURL = URL.createObjectURL(file);
    // remove thumbnail img because it's for the heic file before conversion and errors
    $(dropzone.previewsContainer).find('[data-dz-thumbnail]').remove()

    fetch(blobURL)
      .then((res) => res.blob())
      .then((blob) => heic2any({ blob, toType: 'image/jpeg' }))
      .then((conversionResult: Blob) => {
        // extension of new file will still be .heic on front end if not changed here
        const newFileName = file.name.match(/(.*)\..+/)[1] + '.jpeg'
        dropzone.addFile(new File([conversionResult], newFileName))
      })
      .catch((e) => {
        console.log(e);
      });
  }

  /**
   * heic files in docker do not have a #type for some reason. Fall back to testing file extension
   * @param file
   */
  function isHEIC(file): boolean {
    if (file.type) {
      return file.type === 'image/heic' || file.type === 'image/heif'
    } else {
      return file.name.endsWith('.heic') || file.name.endsWith('.heif')
    }
  }

</script>
